import {observer} from 'mobx-react-lite'
import React, {useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Backend} from '@common/backend'
import {IconButtonNew, LinkButtonNew} from '@components/common/base/buttons'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {Switcher} from '@components/common/base/switcher/switcher'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {useTimelineConfig, useTimelineNewStore} from './timeline-new-store'
import {timelineScales} from './types'
import {getScaleTitle} from './utils'

const TwControls = tw.div`
  h-32
  max-h-32
  px-16
  py-8
  mr-32

  flex
  items-center

  flex-none
  bg-background-sticker-panel
  rounded-8
`

export interface ControlsProps {
  className?: string
}

const ScaleDropdown = observer(({className}: {className?: string}) => {
  const [state, store] = useTimelineNewStore()
  const ref = useRef<HTMLElement>(null)
  const [visible, setVisible] = useState(false)

  const items: MenuItemData[] = timelineScales.map(scale => ({
    id: scale,
    title: getScaleTitle(scale),
    onClick: () => store.setScale(scale)
  }))

  return (
    <>
      <LinkButtonNew
        ref={ref}
        className={className}
        iconAfterClassName="ml-auto"
        icon="IconZoomInArea"
        iconAfter={visible ? 'IconCustomChevronUp' : 'IconCustomChevronDown'}
        color="icon-primary"
        title={getScaleTitle(state.scale)}
        onClick={() => setVisible(x => !x)}
        forcedState={visible ? 'active' : undefined}
      />
      <Dropdown
        reference={ref}
        visible={visible}
        hideArrow
        offset={[0, 10]}
        theme="new-menu"
        className="min-w-136"
        content={<Menu items={items} onItemClicked={() => setVisible(false)} />}
        onVisibleChange={setVisible}
      />
    </>
  )
})

export const TimelineControls = observer((props: ControlsProps) => {
  const {className} = props
  const [state, store] = useTimelineNewStore()
  const config = useTimelineConfig()

  return (
    <TwControls className={className}>
      <ScaleDropdown className="cursor-pointer" />

      <div className="flex gap-8 ml-16">
        <IconButtonNew
          icon="IconMinus"
          onClick={store.onNextScale}
          disabled={state.scale === 'years'}
        />
        <LinkButtonNew
          className="cursor-pointer"
          color="icon-primary"
          title={Lang.msg.timeline.scrollToToday}
          onClick={store.graphHelper.scrollToToday}
        />
        <IconButtonNew
          icon="IconPlus"
          onClick={store.onPrevScale}
          disabled={state.scale === 'days'}
        />
      </div>

      {Backend.features.ganttAutoPlanning && !config.static && (
        <Tooltip
          content={Lang.msg.timeline.autoPlanningTooltip}
          placement="bottom"
          offset={[0, 6]}
        >
          {/* A wrapper div allows to show a tooltip even if the switcher is disabled */}
          <div className="flex flex-none items-center ml-24">
            <Switcher
              active={state.autoPlanning}
              title={Lang.msg.timeline.settingsAutoPlanning}
              onClick={store.toggleAutoPlanning}
              disabled={!store.canEditAutoPlanning}
            />
          </div>
        </Tooltip>
      )}
    </TwControls>
  )
})
